/**
 * <article-toc> — Blog article table of contents.
 *
 * Scans the rendered `.v2-article-content__body` for H2 "chapter" headings
 * (which come from {{ article.content }} and have no ids of their own),
 * injects slugified ids, and builds a navigation list.
 *
 * Desktop (>=1024px): the nav is a sticky sidebar (layout handled in CSS).
 * Mobile (<1024px): the nav is a dropdown toggled by its button.
 *
 * Clicking a chapter smooth-scrolls to that heading, offsetting for the
 * (possibly fixed) site header. No scroll-spy / active tracking.
 */
class ArticleTocElement extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('.v2-article-content__body');
    this.nav = this.querySelector('[data-toc-nav]');
    this.panel = this.querySelector('.v2-article-content__toc-panel');
    this.toggle = this.querySelector('.v2-article-content__toc-toggle');
    this.inner = this.querySelector('.v2-article-content__inner');
    if (!this.body || !this.nav || !this.panel) return;

    this.onClick = this.onClick.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.build();
  }

  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  build() {
    const headings = Array.from(this.body.querySelectorAll('h2'));

    // A single-item (or empty) table of contents is just noise.
    if (headings.length < 2) {
      if (this.inner) this.inner.classList.remove('v2-article-content__inner--has-toc');
      return;
    }

    const used = new Set();
    const ul = document.createElement('ul');
    ul.className = 'v2-article-content__toc-list';

    headings.forEach((heading) => {
      const id = this.ensureId(heading, used);

      const li = document.createElement('li');
      li.className = 'v2-article-content__toc-item';

      const a = document.createElement('a');
      a.className = 'v2-article-content__toc-link';
      a.href = '#' + id;
      a.textContent = (heading.textContent || '').trim();

      li.appendChild(a);
      ul.appendChild(li);
    });

    // Idempotent — clears any prior render when re-run (e.g. theme editor).
    this.panel.replaceChildren(ul);

    // Reveal now that we know there is a real list to show.
    this.nav.hidden = false;

    this.nav.addEventListener('click', this.onClick);
    if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
    document.addEventListener('click', this.onOutsideClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  /** Assign (or reuse) a unique id on a heading and return it. */
  ensureId(heading, used) {
    if (heading.id) {
      used.add(heading.id);
      return heading.id;
    }

    let base = (heading.textContent || '')
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // strip punctuation / emoji
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');

    if (!base) base = 'section';

    let id = base;
    let n = 2;
    while (used.has(id) || document.getElementById(id)) {
      id = base + '-' + n;
      n += 1;
    }

    used.add(id);
    heading.id = id;
    return id;
  }

  onClick(event) {
    const link = event.target.closest('.v2-article-content__toc-link');
    if (!link || !this.nav.contains(link)) return;

    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = target.getBoundingClientRect().top + window.scrollY - this.headerOffset();

    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    history.replaceState(null, '', '#' + id);

    // Move focus into the section for keyboard / screen-reader users.
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });

    this.close();
  }

  /** Measured site-header height (it becomes fixed on scroll) plus breathing room. */
  headerOffset() {
    const header = document.querySelector('.v2-header');
    const h = header ? header.getBoundingClientRect().height : 80;
    return Math.round(h + 24);
  }

  onToggle() {
    if (this.nav.classList.contains('is-open')) this.close();
    else this.open();
  }

  open() {
    this.nav.classList.add('is-open');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.nav.classList.remove('is-open');
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
  }

  onOutsideClick(event) {
    if (this.nav.classList.contains('is-open') && !this.nav.contains(event.target)) {
      this.close();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.nav.classList.contains('is-open')) {
      this.close();
      if (this.toggle) this.toggle.focus();
    }
  }
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleTocElement);
}
